/**
 * Numéro 1 du programme pédagogique ETM — La route.
 *
 * Thème ETM 'R'. Signalisation, types de voies, marquages au sol.
 * Le numéro fondateur : apprendre à "lire" la route comme un motard expérimenté.
 */

import { Map, AlertTriangle, Lightbulb, Quote, Users } from 'lucide-react';

export const routeEtmIssue = {
  slug: 'route',
  themeCode: 'R',
  issueNumber: 1,
  totalIssues: 9,
  category: 'La route',
  shortTitle: 'La route',
  title: 'Lire la route comme un motard',
  kicker: 'Numéro 1 du programme pédagogique ETM',
  subtitle:
    "Signalisation, types de voies, marquages au sol — décoder ce que le bitume te dit. Le numéro fondateur qui distingue le motard du conducteur du dimanche.",
  heroImage: '/img/magazines/route.jpg',
  heroImageAlt: 'Route avec panneau de signalisation triangulaire (virage à gauche) et arbres en bordure',
  readTimeMin: 12,

  seoDescription:
    "La route pour ETM 2026 : signalisation, panneaux, types de voies, marquages au sol, limitations. Lecture pédagogique + QCM final auto-évalué.",
  seoKeywords:
    'signalisation moto, marquages au sol, panneaux ETM, types de voies, limitations vitesse moto, ETM route',

  edito: {
    title: 'Pourquoi savoir lire la route te sauve avant même de rouler',
    body: "Un motard expérimenté <strong>anticipe à 200 mètres devant lui</strong>. Un débutant lit la route à 20 mètres. C'est cette différence qui sauve. Apprendre à décoder les panneaux, les marquages, le type de voie sur laquelle tu roules, c'est <strong>gagner du temps cérébral</strong> pour anticiper les pièges. Ce numéro de 12 minutes te donne la grammaire visuelle de la route française. À la fin, le QCM te dit si tu sais vraiment lire ton environnement.",
    signedBy: 'La rédaction Code 2 Roues',
  },

  sections: [
    {
      type: 'lesson',
      icon: Map,
      partLabel: 'Chapitre 1',
      title: 'Les panneaux : 5 familles, 5 formes',
      paragraphs: [
        "La signalisation routière française obéit à une grammaire stricte. Chaque <strong>forme + couleur</strong> a un sens immédiat — pas besoin de lire le pictogramme pour savoir si c'est dangereux.",
        "<strong>Triangle pointe en haut, fond blanc, bord rouge → Danger</strong> (A1, A2…). Tu dois lever le pied et préparer la manœuvre adaptée. Catégorie la plus large : virages, animaux, gravillons, vent, etc.",
        "<strong>Disque rouge → Interdiction</strong> (B1, B2…). Tu ne peux PAS faire ce qui est barré. Sens interdit, vitesse maximale, dépassement interdit. À moto, attention au B2c (interdiction de dépasser) qui inclut les 2-roues.",
        "<strong>Disque bleu plein → Obligation</strong> (B21, B22…). Tu DOIS faire ce qui est représenté. Direction obligatoire, voie réservée bus/vélos, vitesse minimale.",
        "<strong>Carré ou rectangle bleu → Indication</strong> (C1, C5…). Information utile mais sans obligation : passage piéton, parking, stationnement, sens unique.",
        "<strong>Hexagone (STOP) ou triangle pointe en bas (Cédez le passage) → Priorité</strong>. STOP impose l'arrêt complet pieds au sol. Cédez le passage = ralentir et céder, sans s'arrêter si voie libre.",
      ],
      keyPoints: [
        'Triangle + rouge = Danger (anticiper)',
        'Disque rouge = Interdiction (ne pas faire)',
        'Disque bleu = Obligation (faire)',
        'Carré bleu = Indication (info)',
        'STOP = arrêt complet pieds au sol',
      ],
    },

    {
      type: 'didyouknow',
      icon: Lightbulb,
      title: 'Le saviez-vous ?',
      body: "Le panneau <strong>« STOP » est le seul</strong> en France à imposer un arrêt complet, peu importe qu'il y ait ou non un véhicule prioritaire en face. Un STOP « marqué » (sans arrêt total, juste ralentir) = <strong>infraction de 4e classe : 135 € + 4 points retirés</strong>. Et oui, ça compte même à 3h du matin sur une route déserte. L'agent qui te contrôlera ne fera aucune différence avec un STOP en heure de pointe.",
    },

    {
      type: 'lesson',
      icon: Map,
      partLabel: 'Chapitre 2',
      title: 'Les marquages au sol : ce que le bitume te crie',
      paragraphs: [
        "Les <strong>marquages au sol</strong> complètent la signalisation verticale. Ils sont souvent ignorés alors qu'ils contiennent une mine d'informations sur les manœuvres autorisées.",
        "<strong>Ligne blanche continue</strong> : interdiction absolue de la chevaucher ou la franchir. Dépassement interdit, changement de voie interdit. Sanction : 135 € + 3 points.",
        "<strong>Ligne blanche discontinue</strong> : tu peux franchir pour dépasser ou changer de voie, à condition que la manœuvre soit sans danger.",
        "<strong>Ligne continue + discontinue côte à côte</strong> : tu obéis à celle qui est de TON côté. Si la continue est à ta gauche : interdiction de dépasser. Si la discontinue est à ta gauche : dépassement autorisé.",
        "<strong>Bande d'arrêt d'urgence (BAU)</strong> (autoroute) : interdiction stricte d'y rouler sauf urgence vitale. Sanction : 135 € + 3 points. Les motos ne peuvent PAS l'utiliser pour dépasser.",
        "<strong>Voie de bus / couloir réservé</strong> : interdiction d'y rouler sauf si le panneau M9v autorise les 2-roues motorisés. À Paris, la majorité des couloirs bus sont autorisés aux motos > 125 cm³ depuis 2020.",
        "<strong>Marquages jaunes</strong> : signalisation provisoire (travaux). Ils REMPLACENT temporairement les marquages blancs habituels. Tu DOIS suivre les jaunes.",
      ],
      keyPoints: [
        'Ligne continue = INTERDIT de franchir ou chevaucher',
        'Ligne discontinue = autorisé sous condition de sécurité',
        'BAU autoroute = JAMAIS pour dépasser',
        'Marquages jaunes (travaux) > marquages blancs',
      ],
    },

    {
      type: 'trap',
      icon: AlertTriangle,
      title: 'Le piège à éviter',
      body: "« Les motos peuvent rouler entre les files, c'est légal. » <strong>FAUX en règle générale.</strong> La <strong>circulation inter-files (CIF)</strong> est strictement réglementée. Depuis janvier 2025, elle est <strong>expérimentée dans 21 départements</strong> (Île-de-France, PACA, Rhône, etc.) sur autoroutes et 2x2 voies où la vitesse autorisée est ≥ 70 km/h, dans des conditions strictes : trafic dense ralenti ou arrêté, vitesse motard < 50 km/h, dépassement de la vitesse des autres véhicules < 30 km/h. <strong>Hors des départements expérimentaux ou hors conditions = 135 € + 3 points</strong>. Et même en CIF autorisée : la chute en inter-files est responsabilité totale du motard.",
    },

    {
      type: 'lesson',
      icon: Map,
      partLabel: 'Chapitre 3',
      title: 'Les types de voies : limitations + accès',
      paragraphs: [
        "Toutes les routes ne se valent pas. La <strong>limitation par défaut</strong> dépend du type de voie, et il faut la connaître quand le panneau manque (ce qui arrive après un croisement par exemple).",
        "<strong>Autoroute (A)</strong> : 130 km/h sec, <strong>110 km/h pluie</strong> (90 jeune permis ou si visibilité < 50 m). Accès interdit aux motos < 50 cm³, aux apprentis 2-roues, aux piétons et vélos. Trois voies typiques, BAU à droite.",
        "<strong>Voie rapide / 2x2 voies séparées</strong> : généralement 110 km/h, <strong>100 km/h pluie</strong>. Identifiables au panneau bleu carré (et non vert qui désigne l'autoroute).",
        "<strong>Route à double sens hors agglo</strong> : 80 km/h depuis juillet 2018 sauf certaines routes repassées à 90 (départementales validées par le département, ~50 % du réseau). Vérifier le panneau d'entrée.",
        "<strong>Agglomération</strong> : 50 km/h par défaut (panneau d'entrée d'agglo = ville). De plus en plus de zones <strong>30</strong> ou <strong>20 km/h</strong> (rencontre, piéton prioritaire) dans les centres-villes.",
        "<strong>Cas du permis probatoire (3 premières années)</strong> : 110 sur autoroute (au lieu de 130), 100 sur voie rapide (au lieu de 110), 80 hors agglo (idem), 50 en ville (idem). Ces limitations s'appliquent même quand le réseau autorise plus.",
      ],
      keyPoints: [
        'Autoroute : 130 sec / 110 pluie / 110 jeune permis',
        'Voie rapide 2x2 : 110 sec / 100 pluie',
        'Hors agglo : 80 km/h (parfois 90)',
        'Agglo : 50 km/h, zones 30 fréquentes',
        'Jeune permis = -20 sur autoroute, -10 sur 2x2',
      ],
    },

    {
      type: 'tip',
      icon: Quote,
      title: 'Le conseil du moniteur ECSR',
      body: "« Je dis toujours à mes élèves : <strong>10 secondes en avance</strong>. Sur une 2x2 voies à 110 km/h, tu parcours 300 m en 10 secondes. Donc à chaque virage, chaque sortie, chaque insertion, tu dois avoir <strong>analysé la situation 300 m avant</strong>. Cela veut dire lire le panneau d'annonce, mémoriser la flèche, observer les marquages, anticiper les véhicules. Plus tu lis loin, plus tu rouleras vieux. Le motard mature ne réagit jamais à 10 m — il a déjà choisi sa trajectoire à 300 m. »",
    },

    {
      type: 'story',
      icon: Users,
      title: 'Le témoignage : « Un panneau ignoré, 3 mois d\'arrêt »',
      body: "« Je rentrais d'un week-end, autoroute, beau temps, 130 km/h. Sortie à 800 m, je voulais quitter l'autoroute. <strong>J'avais raté le panneau d'annonce</strong> 1 km avant. À 100 m de la sortie, je me déporte brutalement vers la droite. Une voiture arrivait dans l'angle mort. Choc latéral, chute, 47 km/h. Bilan : clavicule cassée, 3 mois sans moto, +30 % de prime d'assurance. <strong>Une seconde d'inattention sur un panneau, 3 mois de conséquences.</strong> Aujourd'hui, je lis CHAQUE panneau à voix haute dans le casque. Ça paraît bête, mais ça force la concentration. » <em>— Marc, 32 ans</em>",
    },
  ],

  keyStat: {
    kicker: 'Le chiffre à retenir',
    big: '10 sec',
    middle: 'L\'anticipation idéale',
    label: 'qui sépare le motard mature du conducteur qui réagit au dernier moment',
    source: 'Méthode pédagogique REMC 2024',
    accent: '#0891b2',
  },

  glossary: [
    { term: 'Panneau A', def: 'Famille des panneaux de danger : triangle blanc à bord rouge. Anticipation requise, ralentissement souvent nécessaire.' },
    { term: 'Panneau B', def: 'Famille des panneaux d\'interdiction (B1-B19) ou d\'obligation (B21-B59). Cercle rouge pour interdiction, cercle bleu pour obligation.' },
    { term: 'Panneau C', def: 'Famille des panneaux d\'indication. Rectangle ou carré bleu. Information sans obligation.' },
    { term: 'STOP', def: 'Panneau AB4. Hexagone rouge. Impose un arrêt complet (pieds au sol à moto), peu importe la circulation. Infraction = 135 € + 4 points.' },
    { term: 'Cédez le passage', def: 'Panneau AB3a. Triangle blanc pointe en bas. Ralentir et céder, sans arrêt complet si voie libre.' },
    { term: 'BAU', def: 'Bande d\'Arrêt d\'Urgence. Voie à droite sur autoroute, réservée aux situations d\'urgence vitale. Interdite aux motos pour dépasser.' },
    { term: 'CIF', def: 'Circulation Inter-Files. Manœuvre moto entre 2 voies de véhicules en file. Expérimentée dans 21 départements depuis janvier 2025, sous conditions strictes.' },
    { term: 'M9v', def: 'Panonceau additionnel sous certains panneaux de couloir bus, autorisant les 2-roues motorisés (notamment à Paris depuis 2020).' },
    { term: 'Permis probatoire', def: '3 premières années de permis (2 ans si formation post-permis effectuée). Limitations vitesse réduites de 10-20 km/h selon la voie.' },
    { term: 'Zone 30', def: 'Zone urbaine où la vitesse maximale est de 30 km/h. De plus en plus généralisée dans les centres-villes français.' },
  ],

  quiz: {
    title: 'Teste ta compréhension',
    description:
      "10 questions sur la route, la signalisation et les types de voies, tirées de la banque officielle ETM. Tu dois en réussir <strong>au moins 8 sur 10</strong> pour valider ta maîtrise du thème.",
    themeCode: 'R',
    questionsCount: 10,
    passingScore: 8,
  },

  nextIssue: {
    slug: 'circulation',
    number: 2,
    title: 'Priorités, dépassements, intersections',
    short: 'La circulation',
  },
};

export default routeEtmIssue;
